import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { AlignmentTypeahead } from './AlignmentTypeahead';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';
import { useAlignmentRoadmapScope } from './AlignmentRoadmapScopeContext';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  LightBulbIcon,
  LinkIcon,
  LinkSlashIcon
} from '@heroicons/react/24/outline';

interface Vision {
  id: string;
  name: string;
  description?: string;
  vision_type: 'COMPANY' | 'DIVISION' | 'DEPARTMENT' | 'TEAM';
  hierarchy_level: number;
  parent_vision_id?: string;
  owner_user_id: string;
  owner_name: string;
  tenant_id: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  strategy_count: number;
  strategy_names?: string;
  created_at: string;
  updated_at: string;
}

interface VisionFormData {
  name: string;
  description: string;
  parent_vision_id?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
}

export const VisionManagement: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { listParams, scopeKey } = useAlignmentRoadmapScope();
  const [visions, setVisions] = useState<Vision[]>([]);
  const [parentVisions, setParentVisions] = useState<Vision[]>([]);
  const [availableObjectives, setAvailableObjectives] = useState<any[]>([]);
  const [visionObjectiveLinks, setVisionObjectiveLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLinkObjectiveDialogOpen, setIsLinkObjectiveDialogOpen] = useState(false);
  const [editingVision, setEditingVision] = useState<Vision | null>(null);
  const [selectedVisionForLinking, setSelectedVisionForLinking] = useState<Vision | null>(null);
  const [formData, setFormData] = useState<VisionFormData>({
    name: '',
    description: '',
    parent_vision_id: '',
    status: 'ACTIVE'
  });
  const [linkObjectiveFormData, setLinkObjectiveFormData] = useState({
    objective_id: '',
    relationship_type: 'PRIMARY' as 'PRIMARY' | 'SECONDARY' | 'SUPPORTING',
    contribution_level: 'HIGH' as 'HIGH' | 'MEDIUM' | 'LOW',
    notes: ''
  });

  useEffect(() => {
    if (user?.tenantId) {
      fetchVisions();
      fetchAvailableObjectives();
    }
  }, [user?.tenantId, scopeKey]);

  // Fetch vision objective links when visions change
  useEffect(() => {
    if (visions.length > 0) {
      // Clear existing links first
      setVisionObjectiveLinks([]);
      // Fetch links for each vision sequentially
      const fetchAllVisionLinks = async () => {
        for (const vision of visions) {
          await fetchVisionObjectiveLinks(vision.id);
        }
      };
      fetchAllVisionLinks();
    }
  }, [visions]);

  const fetchVisions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/alignment/visions/${user?.tenantId}`, listParams);

      if (response.data.success) {
        setVisions(response.data.data);
        // Set parent visions (excluding the current vision if editing)
        setParentVisions(response.data.data.filter((v: Vision) => 
          !editingVision || v.id !== editingVision.id
        ));
      } else {
        throw new Error(response.data.message || t('alignment.vision.errors.failedToFetchVisions'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    } finally {
      setLoading(false);
    }
  };


  const fetchAvailableObjectives = async () => {
    try {
      const response = await api.get(`/alignment/strategic-objectives/${user?.tenantId}`, listParams);
      if (response.data.success) {
        setAvailableObjectives(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch objectives:', err);
    }
  };


  const fetchVisionObjectiveLinks = async (visionId: string) => {
    try {
      const response = await api.get(`/alignment/vision-objectives/${visionId}`);
      if (response.data.success) {
        // Append new links to existing ones instead of overwriting
        setVisionObjectiveLinks(prevLinks => {
          // Remove any existing links for this vision
          const filteredLinks = prevLinks.filter(link => link.vision_id !== visionId);
          // Add new links for this vision
          return [...filteredLinks, ...response.data.data];
        });
      }
    } catch (err) {
      console.error('Error fetching vision objective links:', err);
    }
  };

  const handleCreateVision = async () => {
    try {
      const response = await api.post('/alignment/visions', {
        ...formData,
        owner_user_id: user?.id,
        tenant_id: user?.tenantId,
      });

      if (response.data.success) {
        setIsCreateDialogOpen(false);
        setFormData({
          name: '',
          description: '',
          parent_vision_id: '',
          status: 'ACTIVE'
        });
        fetchVisions();
      } else {
        throw new Error(response.data.message || t('alignment.vision.errors.failedToCreateVision'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    }
  };

  const handleUpdateVision = async () => {
    if (!editingVision) return;

    try {
      const response = await api.put(`/alignment/visions/${editingVision.id}`, {
        ...formData,
        parent_vision_id: formData.parent_vision_id || null,
      });

      if (response.data.success) {
        setIsEditDialogOpen(false);
        setEditingVision(null);
        setFormData({
          name: '',
          description: '',
          parent_vision_id: '',
          status: 'ACTIVE'
        });
        fetchVisions();
      } else {
        throw new Error(response.data.message || t('alignment.vision.errors.failedToUpdateVision'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    }
  };

  const handleDeleteVision = async (visionId: string) => {
    if (!confirm(t('alignment.vision.deleteConfirm'))) {
      return;
    }

    try {
      const response = await api.delete(`/alignment/visions/${visionId}`);

      if (response.data.success) {
        fetchVisions();
      } else {
        throw new Error(response.data.message || t('alignment.vision.errors.failedToDeleteVision'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    }
  };


  const handleLinkObjective = async () => {
    if (!selectedVisionForLinking || !linkObjectiveFormData.objective_id) return;

    
    try {
      const response = await api.post('/alignment/vision-objective-links', {
        vision_id: selectedVisionForLinking.id,
        objective_id: linkObjectiveFormData.objective_id,
        relationship_type: linkObjectiveFormData.relationship_type,
        contribution_level: linkObjectiveFormData.contribution_level,
        notes: linkObjectiveFormData.notes,
        tenant_id: user?.tenantId,
      });

      if (response.data.success) {
        setIsLinkObjectiveDialogOpen(false);
        setSelectedVisionForLinking(null);
        setLinkObjectiveFormData({
          objective_id: '',
          relationship_type: 'PRIMARY',
          contribution_level: 'HIGH',
          notes: ''
        });
        fetchVisions(); // Refresh to show updated objective count
        fetchVisionObjectiveLinks(selectedVisionForLinking.id); // Refresh objective links
      } else {
        throw new Error(response.data.message || t('alignment.vision.errors.failedToLinkObjective'));
      }
    } catch (err) {
      // Handle specific error cases
      if (err instanceof Error) {
        if (err.message.includes('already exists')) {
          setError(t('alignment.vision.errors.linkAlreadyExists'));
        } else {
          setError(err.message);
        }
      } else {
        setError(t('alignment.common.errors.anErrorOccurred'));
      }
    }
  };

  const handleDeleteObjectiveLink = async (linkId: string) => {
    if (!confirm(t('alignment.vision.deleteLinkConfirm'))) {
      return;
    }

    try {
      const response = await api.delete(`/alignment/vision-objective-links/${linkId}`);
      
      if (response.data.success) {
        // Remove the link from local state
        setVisionObjectiveLinks(prevLinks => 
          prevLinks.filter(link => link.id !== linkId)
        );
        fetchVisions(); // Refresh to show updated objective count
      } else {
        throw new Error(response.data.message || t('alignment.vision.errors.failedToDeleteLink'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    }
  };


  const openEditDialog = (vision: Vision) => {
    setEditingVision(vision);
        setFormData({
          name: vision.name,
          description: vision.description || '',
          parent_vision_id: vision.parent_vision_id || '',
          status: vision.status
        });
    setIsEditDialogOpen(true);
  };

  const openCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
      parent_vision_id: '',
      status: 'ACTIVE'
    });
    setIsCreateDialogOpen(true);
  };


  const openLinkObjectiveDialog = (vision: Vision) => {
    setSelectedVisionForLinking(vision);
    setLinkObjectiveFormData({
      objective_id: '',
      relationship_type: 'PRIMARY',
      contribution_level: 'HIGH',
      notes: ''
    });
    setIsLinkObjectiveDialogOpen(true);
  };

  const closeDialogs = () => {
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsLinkObjectiveDialogOpen(false);
    setEditingVision(null);
    setSelectedVisionForLinking(null);
    setFormData({
      name: '',
      description: '',
      parent_vision_id: '',
      status: 'ACTIVE'
    });
    setLinkObjectiveFormData({
      objective_id: '',
      relationship_type: 'PRIMARY',
      contribution_level: 'HIGH',
      notes: ''
    });
  };

  const relationshipTypeOptions = [
    { value: 'PRIMARY', label: t('alignment.vision.relationshipTypes.primary') },
    { value: 'SECONDARY', label: t('alignment.vision.relationshipTypes.secondary') },
    { value: 'SUPPORTING', label: t('alignment.vision.relationshipTypes.supporting') }
  ];

  const contributionLevelOptions = [
    { value: 'HIGH', label: t('alignment.vision.contributionLevels.high') },
    { value: 'MEDIUM', label: t('alignment.vision.contributionLevels.medium') },
    { value: 'LOW', label: t('alignment.vision.contributionLevels.low') }
  ];

  if (loading) {
    return (
      <Card padding="none">
        <CardContent className="p-3">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-end">
        <Button onClick={openCreateDialog} className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
          <PlusIcon className="w-4 h-4 mr-2" />
          {t('alignment.vision.createNew')}
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <span className="text-sm">⚠️</span>
            <span className="text-sm font-medium">{t('alignment.common.error')}</span>
          </div>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}

      {/* Visions List */}
      <div className="grid gap-2">
        {visions.map((vision) => (
          <Card key={vision.id} padding="none" className="hover:shadow-md transition-shadow">
            <CardContent className="p-3">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="mb-1">
                    <h3
                      className="text-sm font-semibold text-[var(--color-surface-text)]"
                      title={`${t('alignment.vision.owner')}: ${vision.owner_name} · ${t('alignment.vision.created')}: ${new Date(vision.created_at).toLocaleDateString()}`}
                    >
                      {vision.name}
                    </h3>
                  </div>
                  
                  {vision.description && (
                    <p className="text-sm text-[var(--color-surface-text-secondary)] mb-1">
                      {vision.description}
                    </p>
                  )}

                  {visionObjectiveLinks.filter(link => link.vision_id === vision.id).length > 0 && (
                    <div className="mt-1">
                      <div className="text-xs font-medium text-[var(--color-surface-text)] mb-0.5">
                        {t('alignment.vision.linkedObjectives')}
                      </div>
                      <ul className="list-disc pl-5 text-sm text-[var(--color-surface-text)]">
                        {visionObjectiveLinks
                          .filter(link => link.vision_id === vision.id)
                          .map((link) => (
                            <li key={link.id} className="leading-5">
                              <span className="inline-flex items-center gap-1">
                                {link.objective_name}
                                <button
                                  type="button"
                                  onClick={() => handleDeleteObjectiveLink(link.id)}
                                  className="p-0.5 text-red-600 hover:text-red-700 flex-shrink-0"
                                  title={t('alignment.vision.unlink') || 'Unlink'}
                                  aria-label={t('alignment.vision.unlink') || 'Unlink'}
                                >
                                  <LinkSlashIcon className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                </div>

                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openLinkObjectiveDialog(vision)}
                    className="text-green-600 hover:text-green-700"
                  >
                    <LinkIcon className="w-4 h-4 mr-1" />
                    {t('alignment.vision.linkObjective')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(vision)}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteVision(vision.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {visions.length === 0 && (
          <Card padding="none">
            <CardContent className="p-4 text-center">
              <div className="text-[var(--color-surface-text-secondary)]">
                <LightBulbIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">{t('alignment.noVisionsCreated')}</h3>
                <p className="mb-4">{t('alignment.noVisionsDescription')}</p>
                <Button onClick={openCreateDialog} className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {t('alignment.createYourFirstVision')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Vision Dialog */}
      {isCreateDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('alignment.vision.dialogTitles.createNew')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.visionName')}</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('alignment.visionManagement.enterVisionName')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.description')}</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('alignment.visionManagement.describeVision')}
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.parentVisionOptional')}</label>
                <AlignmentTypeahead
                  value={formData.parent_vision_id || ''}
                  onChange={(id) => setFormData({ ...formData, parent_vision_id: id })}
                  options={parentVisions.map(v => ({ id: v.id, label: v.name }))}
                  placeholder={t('alignment.vision.noParentVision')}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={closeDialogs} variant="outline" className="flex-1">
                {t('alignment.vision.buttonLabels.cancel')}
              </Button>
              <Button onClick={handleCreateVision} className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                {t('alignment.vision.buttonLabels.createVision')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Vision Dialog */}
      {isEditDialogOpen && editingVision && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('alignment.vision.dialogTitles.edit')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.visionName')}</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('alignment.visionManagement.enterVisionName')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.description')}</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('alignment.visionManagement.describeVision')}
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.parentVisionOptional')}</label>
                <AlignmentTypeahead
                  value={formData.parent_vision_id || ''}
                  onChange={(id) => setFormData({ ...formData, parent_vision_id: id })}
                  options={parentVisions.map(v => ({ id: v.id, label: v.name }))}
                  placeholder={t('alignment.vision.noParentVision')}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={closeDialogs} variant="outline" className="flex-1">
                {t('alignment.vision.buttonLabels.cancel')}
              </Button>
              <Button onClick={handleUpdateVision} className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                {t('alignment.vision.buttonLabels.updateVision')}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Link Objective Dialog */}
      {isLinkObjectiveDialogOpen && selectedVisionForLinking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('alignment.vision.dialogTitles.linkObjectiveToVision')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('alignment.vision.linkingObjective')}: <strong>{selectedVisionForLinking.name}</strong>
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.selectObjective')}</label>
                <AlignmentTypeahead
                  value={linkObjectiveFormData.objective_id}
                  onChange={(id) => setLinkObjectiveFormData({ ...linkObjectiveFormData, objective_id: id })}
                  options={availableObjectives.map(o => ({ id: o.id, label: o.name }))}
                  placeholder={t('alignment.vision.placeholders.chooseObjective')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.relationshipType')}</label>
                <Select
                  value={linkObjectiveFormData.relationship_type}
                  onChange={(e) => setLinkObjectiveFormData({ ...linkObjectiveFormData, relationship_type: e.target.value as any })}
                  options={relationshipTypeOptions}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.contributionLevel')}</label>
                <Select
                  value={linkObjectiveFormData.contribution_level}
                  onChange={(e) => setLinkObjectiveFormData({ ...linkObjectiveFormData, contribution_level: e.target.value as any })}
                  options={contributionLevelOptions}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.vision.formLabels.notesOptional')}</label>
                <Textarea
                  value={linkObjectiveFormData.notes}
                  onChange={(e) => setLinkObjectiveFormData({ ...linkObjectiveFormData, notes: e.target.value })}
                  placeholder={t('alignment.vision.placeholders.addNotesAboutLink')}
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={closeDialogs} variant="outline" className="flex-1">
                {t('alignment.vision.buttonLabels.cancel')}
              </Button>
              <Button 
                onClick={handleLinkObjective} 
                disabled={!linkObjectiveFormData.objective_id}
                className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]"
              >
                {t('alignment.vision.buttonLabels.linkObjective')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
